/* ==================================================================
   The Golden Quest · mock campaign response

   The `goldenQuest` object GET /golden-quest returns (gosats-apis,
   goSatsCampaignRoot). Outside production builds, `?scenario=<name>` on the
   URL makes api.js serve one of the presets below instead of calling the
   backend (an unknown name falls back to GQ_MOCK_SCENARIO), so every flow
   previews in a plain browser without a session token.

   Presets:
     pre-ftu     PRE_LAUNCH  first open: story → pre-registration → hub
     pre-new     PRE_LAUNCH  story seen, Gems not claimed: hub with "Claim Now"
     pre-reg     PRE_LAUNCH  registered: 5 Gems reserved, reminder set
     launch      LIVE        launch day: go-live screen, 5 Gems ready to open
     live-mid    LIVE        Card done, Vouchers 2/4, Referrals done +2 Gems
     live-chest  LIVE        Card just completed: chest celebration on open
     live-edge   LIVE        one step from a chest: Card 3/4
     ended       ENDED       you won Grand 3: results sheet
     ended-none  ENDED       no chests: "No grand prize this time"

   What drives which screen
     story.enabled && !user.storySeen    "How to play" story on open
     PRE_LAUNCH && !user.preRegistered   pre-registration after the story
     LIVE && !user.liveIntroSeen         "The Golden Quest is open", once
     wallet.chests[].celebrated = false  "Gold Chest earned" celebration
     draw (ENDED only)                   "See the winners" results sheet

   Values
     status                 PRE_LAUNCH | LIVE | ENDED (omit to derive from the dates)
     dates                  epoch ms as the backend sends them, or ISO 8601 with the IST offset
     gems[].reward          null until the backend's reward selection lands → shows as pending
     streaks[].enabled      false → the card is locked (chip + "Unlocks" CTA); not tied to the dates
     streaks[].cta.action   ADD_MONEY | VOUCHERS | BUY_GOLD | REFER
     gems[].source          PRE_REG | CARD | VOUCHER | GOLD | REFERRAL
     gems[].reward.kind     GOLD | SATS | VOUCHER | CASHBACK | BOOST
     chests[].streak        card | voucher | gold | ref
     chests[].result.kind   GRAND | SATS | GOLD
   ================================================================== */

export const GQ_MOCK_SCENARIO = 'pre-ftu';

/* ---------- the four streaks ----------
   `progress` is completed steps (0–4); `extraGems` is referral Gems earned
   past the chest. Presets change only those. */
const CARD = {
  key: 'card', title: 'Card', text: 'Your card already earns Bitcoin and Gold, it now earns gems with even greater prizes!',
  labels: ['₹0', '₹500', '₹1000', '₹1500', '₹2000'], cta: { text: 'Add Money', action: 'ADD_MONEY' },
  enabled: true, progress: 0, completed: false, extraGems: 0,
};
const VOUCHERS = {
  key: 'voucher', title: 'Vouchers', text: 'Shop the biggest brands and earn Gems.',
  labels: ['₹0', '₹500', '₹1000', '₹1500', '₹2000'], cta: { text: 'Buy Vouchers', action: 'VOUCHERS' },
  enabled: true, progress: 0, completed: false, extraGems: 0,
};
const GOLD = {
  key: 'gold', title: 'Gold', text: 'Buy gold, build wealth and earn gems',
  labels: ['₹0', '₹500', '₹1000', '₹1500', '₹2000'], cta: { text: 'Buy Gold', action: 'BUY_GOLD' },
  enabled: true, progress: 0, completed: false, extraGems: 0,
};
const REFERRALS = {
  key: 'ref', title: 'Referrals', text: 'Refer a friend, when they make a transaction, buy gold or a voucher you get gems.',
  labels: ['0', '1', '2', '3', '4'], cta: { text: 'Refer a Friend', action: 'REFER' },
  enabled: true, progress: 0, completed: false, extraGems: 0,
};

/* ---------- the five pre-registration Gems ---------- */
/* credited at launch, not opened yet */
const PRE_REG_GEMS = [
  { id: 'g1', source: 'PRE_REG', earnedAt: '2026-10-18T00:00:00+05:30', openedAt: null, reward: null },
  { id: 'g2', source: 'PRE_REG', earnedAt: '2026-10-18T00:00:00+05:30', openedAt: null, reward: null },
  { id: 'g3', source: 'PRE_REG', earnedAt: '2026-10-18T00:00:00+05:30', openedAt: null, reward: null },
  { id: 'g4', source: 'PRE_REG', earnedAt: '2026-10-18T00:00:00+05:30', openedAt: null, reward: null },
  { id: 'g5', source: 'PRE_REG', earnedAt: '2026-10-18T00:00:00+05:30', openedAt: null, reward: null },
];
/* the same five, opened on launch morning */
const PRE_REG_GEMS_OPENED = [
  { id: 'g1', source: 'PRE_REG', earnedAt: '2026-10-18T00:00:00+05:30', openedAt: '2026-10-18T08:10:00+05:30', reward: { name: '₹25 in Gold', detail: 'Added to your gold vault', kind: 'GOLD' } },
  { id: 'g2', source: 'PRE_REG', earnedAt: '2026-10-18T00:00:00+05:30', openedAt: '2026-10-18T08:11:00+05:30', reward: { name: '100 Sats', detail: 'Added to your Bitcoin balance', kind: 'SATS' } },
  { id: 'g3', source: 'PRE_REG', earnedAt: '2026-10-18T00:00:00+05:30', openedAt: '2026-10-18T08:12:00+05:30', reward: { name: '₹100 Amazon voucher', detail: 'Saved under Vouchers', kind: 'VOUCHER' } },
  { id: 'g4', source: 'PRE_REG', earnedAt: '2026-10-18T00:00:00+05:30', openedAt: '2026-10-18T08:13:00+05:30', reward: { name: '₹50 in Gold', detail: 'Added to your gold vault', kind: 'GOLD' } },
  { id: 'g5', source: 'PRE_REG', earnedAt: '2026-10-18T00:00:00+05:30', openedAt: '2026-10-18T08:14:00+05:30', reward: { name: '₹10 cashback', detail: 'Credited to your card', kind: 'CASHBACK' } },
];

/* ---------- grand prizes (the same in every preset) ---------- */
const GRAND_PRIZES = {
  total: 23,
  drawOn: '2026-11-13T20:00:00+05:30',
  /* the three on the hub; `art` picks the artwork: iphone | ps5 | watch2 */
  top: [
    { art: 'iphone', name: 'iPhone Duo', sub: '256 GB', rank: '1st winner', badge: 'GRAND PRIZE' },
    { art: 'ps5', name: 'PlayStation 5', sub: 'Console', rank: '2nd winner' },
    { art: 'watch2', name: 'Apple Watch SE', sub: 'GPS', rank: '3rd winner' },
  ],
  /* the full list, for the prizes sheet */
  all: [
    { label: 'Grand 1', name: 'iPhone Duo', sub: '256 GB', winners: 1, art: 'iphone' },
    { label: 'Grand 2', name: 'PlayStation 5', sub: 'Console', winners: 1, art: 'ps5' },
    { label: 'Grand 3', name: 'Apple Watch SE', sub: 'GPS', winners: 1, art: 'watch2' },
    { label: 'Grand 4', name: 'Samsung Galaxy M17e', sub: 'Smartphone', winners: 1 },
    { label: 'Grand 5', name: 'Redmi Pad 2', sub: 'Tablet', winners: 1 },
    { label: 'Grand 6', name: 'AirPods 5', sub: 'Earbuds', winners: 1 },
    { label: 'Grand 7–8', name: 'OnePlus Watch 2R', sub: 'Smartwatch', winners: 2 },
    { label: 'Grand 9–10', name: 'JBL Flip Essential 2', sub: 'Speaker', winners: 2 },
    { label: 'Grand 11–12', name: 'Sony WH-CH520', sub: 'Wireless headphones', winners: 2 },
    { label: 'Grand 13–14', name: 'JBL Go 4', sub: 'Speaker', winners: 2 },
    { label: 'Grand 15–18', name: 'Fastrack Astor FR2', sub: 'Smartwatch', winners: 4 },
    { label: 'Grand 19–21', name: 'OnePlus Nord Buds', sub: 'Earbuds', winners: 3 },
    { label: 'Grand 22–23', name: 'Ambrane 10000mAh', sub: 'Power bank', winners: 2 },
  ],
  /* every other chest wins Sats or Gold */
  tiers: [
    { code: 'C7', name: '1 gm Gold', winners: 2 },
    { code: 'C6', name: 'Sats or Gold', winners: 10 },
    { code: 'C5', name: 'Sats or Gold', winners: 60 },
    { code: 'C4', name: 'Sats or Gold', winners: 250 },
    { code: 'C3', name: 'Sats or Gold', winners: 560 },
    { code: 'C2', name: 'Sats or Gold', winners: 2127 },
    { code: 'C1', name: 'Sats or Gold', winners: 2968 },
  ],
  tierWinners: 5977,
  totalPrizes: 6000,
};

/* ---------- grand draw winners (ENDED) ---------- */
const WINNERS = [
  { name: 'Pr***a S.', city: 'Pune', prize: 'iPhone Duo' },
  { name: 'Ro***n M.', city: 'Hyderabad', prize: 'PlayStation 5' },
  { name: 'Ar***n K.', city: 'Bengaluru', prize: 'Apple Watch SE' },
  { name: 'Ka***n D.', city: 'Mumbai', prize: 'Samsung Galaxy M17e' },
  { name: 'Ne***a R.', city: 'Delhi', prize: 'Redmi Pad 2' },
  { name: 'Vi***y T.', city: 'Chennai', prize: 'AirPods 5' },
  { name: 'An***a P.', city: 'Kolkata', prize: 'OnePlus Watch 2R' },
  { name: 'Sa***l G.', city: 'Jaipur', prize: 'OnePlus Watch 2R' },
  { name: 'Me***a K.', city: 'Ahmedabad', prize: 'JBL Flip Essential 2' },
  { name: 'Ha***h B.', city: 'Kochi', prize: 'JBL Flip Essential 2' },
  { name: 'Ri***a N.', city: 'Indore', prize: 'Sony WH-CH520' },
  { name: 'Ta***r S.', city: 'Surat', prize: 'Sony WH-CH520' },
  { name: 'De***a J.', city: 'Lucknow', prize: 'JBL Go 4' },
  { name: 'Yu***f A.', city: 'Bhopal', prize: 'JBL Go 4' },
  { name: 'Ni***l C.', city: 'Nagpur', prize: 'Fastrack Astor FR2' },
  { name: 'Sh***i V.', city: 'Noida', prize: 'Fastrack Astor FR2' },
  { name: 'Ab***k R.', city: 'Guwahati', prize: 'Fastrack Astor FR2' },
  { name: 'Po***a M.', city: 'Chandigarh', prize: 'Fastrack Astor FR2' },
  { name: 'Ga***v L.', city: 'Coimbatore', prize: 'OnePlus Nord Buds' },
  { name: 'Ir***n Q.', city: 'Patna', prize: 'OnePlus Nord Buds' },
  { name: 'Ko***a B.', city: 'Mysuru', prize: 'OnePlus Nord Buds' },
  { name: 'Ma***h P.', city: 'Vizag', prize: 'Ambrane 10000mAh' },
  { name: 'Ze***n I.', city: 'Goa', prize: 'Ambrane 10000mAh' },
];

/* ---------- the response, in full ----------
   PRE_LAUNCH, first open. Every other preset is this object with a few
   top-level fields swapped (status, user, wallet, streaks, draw). */
const BASE = {
  enabled: true,              // false → the page renders nothing
  status: 'PRE_LAUNCH',
  campaignStart: '2026-10-18T00:00:00+05:30', // drives the launch countdown
  campaignEnd: '2026-11-11T23:59:59+05:30',   // grand draw day

  story: {
    enabled: true,            // false → no story on open and "How to play" hidden
  },

  user: {
    preRegistered: false,     // true once the free pre-registration Gems are claimed
    preRegGems: 5,            // how many Gems pre-registration reserves
    reminderSet: false,       // launch reminder for 18 Oct is on
    storySeen: false,         // true → skip the story on open
    liveIntroSeen: false,     // true → skip the "festival is open" screen (LIVE)
    referralCode: 'GSFEST-7K2',
  },

  wallet: {
    gems: [],                 // every Gem the user holds (see PRE_REG_GEMS for the shape)
    chests: [],               // every Gold Chest earned; each is an entry in the grand draw
  },

  /* before launch the backend keeps Card / Vouchers / Gold locked (`enabled:
     false`) and opens Referrals; the card lock follows `enabled`, not the dates */
  streaks: [{ ...CARD, enabled: false }, { ...VOUCHERS, enabled: false }, { ...GOLD, enabled: false }, REFERRALS],

  grandPrizes: GRAND_PRIZES,

  draw: null,                 // ENDED only: who won what (see the `ended` preset)

  share: {
    message: 'I\'m winning BTC, Gold and an iPone Duo, you can too with my code GSFEST-7K2. https://dl.gosats.io/home',
  },

  /* numbered sections; a block is { p }, { ul } (string or { lead, text }
     bullets) or { table: { head, rows } } */
  terms: {
    sections: [
      { title: 'About the contest', blocks: [
        { p: 'The Golden Quest (the "contest") is run by Saffroncoin Solutions Private Limited, operating as GoSats ("we", "us"). It is a contest with random selection. By opting in you accept these terms.' },
      ] },
      { title: 'Dates', blocks: [
        { p: 'All times are IST.' },
        { table: {
          head: ['What', 'When'],
          rows: [
            ['Pre-registration (opt-in) opens', 'Saturday 10 Oct 2026, 4:00 PM'],
            ['Early opt-in bonus', 'Opt in before the contest opens: from 10 Oct 2026, 4:00 PM to 18 Oct 2026, 6:00 PM'],
            ['Contest period', '18 Oct 2026, 6:00 PM to 11 Nov 2026'],
            ['Reveal day (chests unlock)', '13 Nov 2026'],
          ],
        } },
      ] },
      { title: 'Who can take part', blocks: [
        { ul: [
          'GoSats users with completed KYC and access to the GoSats app.',
          '18 years or older and resident in India. Legal to confirm.',
        ] },
      ] },
      { title: 'How to join', blocks: [
        { ul: [
          'Opt in using the button or banner in the GoSats app. Enrolment is not automatic. Opting in is free.',
          'Early opt-in bonus: if you opt in before the contest opens, you get 5 gems. They are credited when you opt in and can be opened once the game goes live on 18 Oct. One bonus per user. Opt-ins after 18 Oct, do not get it.',
          'Spends and contest actions count only while the contest is live, from 18 Oct to its close on 11 Nov. Nothing before or after counts.',
        ] },
      ] },
      { title: 'Earning gems and Gold chests', blocks: [
        { p: 'There are four bars. Every bar works the same way: four gems fill a bar and earn one Gold chest.' },
        { table: {
          head: ['Bar', 'Earn a gem', 'Earn the Gold chest'],
          rows: [
            ['Card', 'Every ₹500 of eligible spends on the GoSats Card', '₹2,000 of eligible spends'],
            ['Vouchers', 'Every ₹500 spent on eligible vouchers', '₹2,000 on eligible vouchers'],
            ['Gold', 'Every ₹500 of Gold bought, one-time or through a SIP', '₹2,000 of Gold'],
            ['Referrals', '1 gem for each friend you refer', '4 friends'],
          ],
        } },
        { p: 'What counts and what does not:' },
        { ul: [
          'A referral counts only once the friend completes a card transaction, a Gold purchase or a voucher purchase. Self-referrals and duplicate accounts do not count.',
          'The SuperCoin portion of any purchase does not count toward a bar.',
          'Wallet loads and wallet-style vouchers do not count.',
          'Eligible card spend categories and eligible voucher brands: [list to be finalised].',
          'Gold bought and then sold during the contest does not count.',
          'Each bar earns one Gold chest at most. The Card, Vouchers and Gold bars stop earning gems once they have given 4. The Referrals bar earns a gem for every friend, with no limit, and its Gold chest at 4 friends.',
        ] },
      ] },
      { title: 'Gems', blocks: [
        { ul: [
          'A gem is a small prize. It opens as soon as it is earned, once the game is live. The 5 early opt-in gems open from 18 Oct.',
          'Every gem has a prize inside: SATs or Gold, your pick, at the same rupee value either way.',
          'Gem prizes are credited to your GoSats account when you open the gem.',
        ] },
      ] },
      { title: 'Gold chests', blocks: [
        { ul: [
          'You earn a Gold chest when a bar is full.',
          'Chests are held until reveal day. They cannot be opened earlier.',
          'Every chest has a prize inside. Some hold devices, and the rest hold SATs or Gold.',
        ] },
      ] },
      { title: 'Prizes', blocks: [
        { ul: [
          { lead: 'Grand prizes:', text: 'devices, such as the iPhone Duo. They are not available as cash, SATs or Gold.' },
          { lead: '1 gm Gold prize:', text: 'some chests hold 1 gm of Gold. This prize is Gold only.' },
          { lead: 'Other chest prizes:', text: 'SATs or Gold, your pick, at the same rupee value either way.' },
          'SATs and Gold are valued in rupees at the rate when the prize is credited to your GoSats account.',
          'Device models, variants and colours depend on availability. We may replace a device with one of equal value. Images are for illustration. Warranty is provided by the manufacturer.',
          'Prizes cannot be transferred or exchanged for cash.',
        ] },
      ] },
      { title: 'Selection and winners', blocks: [
        { ul: [
          'Prizes in Gold chests are assigned by random selection on reveal day. The result is final.',
          'Winners are told in the GoSats app and by push notification.',
          'Device winners must share delivery details within 7 days or the prize is forfeited.',
          '[With your consent, we may show your name in short form and your city when winners are announced. Legal to confirm.]',
        ] },
      ] },
      { title: 'Tax and delivery', blocks: [
        { ul: [
          'Prizes are subject to tax as required by Indian law.',
          'Devices are delivered to addresses in India only.',
        ] },
      ] },
      { title: 'Fair play', blocks: [
        { p: 'We may remove gems, chests and prizes, and disqualify a user, for fake or duplicate accounts, self-referrals, round-trip Gold purchases, automated activity, or any attempt to get around these terms. Our decision is final.' },
      ] },
      { title: 'Changes and cancellation', blocks: [
        { p: 'We may change, pause or end the contest for fraud, technical failure or legal reasons. Changes will be shown in the app.' },
      ] },
      { title: 'Data', blocks: [
        { p: 'We use your data as set out in the GoSats Privacy Policy and only to run the contest.' },
      ] },
      { title: 'SATs and Gold', blocks: [
        { p: 'SATs are units of Bitcoin, which is a virtual digital asset. Gold in the contest is digital Gold held in your GoSats account.' },
      ] },
      { title: 'Governing law', blocks: [
        { p: 'These terms are governed by the laws of India.' },
      ] },
    ],
  },

  faqs: [
    { q: 'What is The Golden Quest?', a: 'A GoSats contest that runs from 18 Oct, 6 PM, to 11 Nov. You fill four bars, earn gems and Gold chests, and open gems as you go. Gold chests unlock together on reveal day, 13 Nov. Some hold big prizes, starting with an iPhone Duo.' },
    { q: 'What is a gem?', a: 'A small prize. You earn one every time you cross ₹500 in a category, and you can open it straight away. Inside is SATs or Gold, your pick.' },
    { q: 'What is a Gold chest?', a: 'You earn one when a bar is full. It is held until reveal day. Every chest has a prize inside. Some hold devices, some hold 1 gm of Gold, and the rest hold SATs or Gold.' },
    { q: 'What are the four bars?', a: 'Card, Vouchers, Gold and Referrals. Each has its own bar and its own Gold chest.' },
    { q: 'How do I join?', a: 'Opt in from the GoSats app. Pre-registration opens on 10 Oct at 4 PM and is free.' },
    { q: 'What do I get for opting in early?', a: 'Opt in before the contest opens on 18 Oct at 6 PM and you get 5 gems. They are waiting for you and open once the game goes live. Opt-ins after that do not get them.' },
    { q: 'Does anything I do before the contest opens count?', a: 'No. Spends and actions count only while the contest is live, from 18 Oct, 6 PM, to 11 Nov, not before and not after.' },
    { q: 'Who can take part?', a: 'GoSats users with completed KYC who have the app. [Age and state rules to confirm.] The full rules are in the Terms and Conditions.' },
    { q: 'How do I earn gems on the Card bar?', a: 'Every ₹500 of eligible spends on your GoSats Card earns a gem. At ₹2,000 the bar is full and you earn a Gold chest.' },
    { q: 'How do I earn gems on the Vouchers bar?', a: 'Every ₹500 spent on eligible vouchers earns a gem. At ₹2,000 you earn a Gold chest. Wallet-style vouchers do not count.' },
    { q: 'How do I earn gems on the Gold bar?', a: 'Every ₹500 of Gold you buy earns a gem, whether it is a one-time purchase or a SIP. At ₹2,000 you earn a Gold chest.' },
    { q: 'How do I earn gems on the Referrals bar?', a: 'You get 1 gem for each friend you refer. A referral counts once your friend completes a card transaction. Four friends fill the bar and earn a Gold chest.' },
    { q: 'What does not count?', a: 'The SuperCoin portion of a purchase, wallet loads, wallet-style vouchers, anything outside eligible categories, and anything outside the contest days.' },
    { q: 'Is there a limit on how much I can earn?', a: 'Each bar earns one Gold chest at most. The Card, Vouchers and Gold bars stop at 4 gems each. The Referrals bar keeps earning a gem for every friend, with no limit, and earns its Gold chest at 4 friends.' },
    { q: 'When can I open my gems?', a: 'As soon as you earn them, once the game is live. Your 5 early opt-in gems open from 18 Oct, 6 PM.' },
    { q: 'Can I choose between SATs and Gold?', a: 'Yes. For gems, and for most chest prizes, you pick SATs or Gold when you open it. The rupee value is the same either way, and the amount of SATs or Gold is set at the rate when the prize is credited to your account. Devices and the 1 gm Gold prize are the exceptions.' },
    { q: 'When do chests open?', a: 'All together on reveal day, 13 Nov. They cannot be opened earlier.' },
    { q: 'Does every chest have a prize?', a: 'Yes. Some hold devices, some hold 1 gm of Gold, and the rest hold SATs or Gold.' },
    { q: 'How are the winners chosen?', a: 'By random selection on reveal day. The result is final.' },
    { q: 'What are the big prizes?', a: 'Devices, starting with an iPhone Duo.' },
    { q: 'How do I claim a device?', a: 'Open your chest on reveal day and we will ask for your delivery details. Share them within 7 days or the prize is forfeited.' },
    { q: 'Can I swap a device for cash?', a: 'No. Prizes cannot be exchanged for cash or transferred.' },
    { q: 'Where do my SATs and Gold go?', a: 'They are added to your GoSats account when you open the gem or chest. SATs are units of Bitcoin, a virtual digital asset.' },
    { q: 'Where can I read the full rules?', a: 'In the Terms and Conditions, linked from the contest page in the app.' },
  ],
};

/* ---------- presets ---------- */

export const GQ_MOCK_RESPONSES = {
  /* PRE_LAUNCH · first open: story → pre-registration → hub */
  'pre-ftu': { goldenQuest: BASE },

  /* PRE_LAUNCH · story seen, Gems not claimed: hub with "Claim Now" */
  'pre-new': {
    goldenQuest: {
      ...BASE,
      user: { preRegistered: false, preRegGems: 5, reminderSet: false, storySeen: true, liveIntroSeen: false, referralCode: 'GSFEST-7K2' },
    },
  },

  /* PRE_LAUNCH · registered: 5 Gems reserved, reminder set */
  'pre-reg': {
    goldenQuest: {
      ...BASE,
      user: { preRegistered: true, preRegGems: 5, reminderSet: true, storySeen: true, liveIntroSeen: false, referralCode: 'GSFEST-7K2' },
      wallet: { gems: PRE_REG_GEMS, chests: [] },
    },
  },

  /* LIVE · launch day: go-live screen, the 5 pre-registration Gems ready to open */
  launch: {
    goldenQuest: {
      ...BASE,
      status: 'LIVE',
      user: { preRegistered: true, preRegGems: 5, reminderSet: true, storySeen: true, liveIntroSeen: false, referralCode: 'GSFEST-7K2' },
      wallet: { gems: PRE_REG_GEMS, chests: [] },
      streaks: [CARD, VOUCHERS, GOLD, REFERRALS],
    },
  },

  /* LIVE · mid campaign: Card done, Vouchers 2/4, Referrals done with 2 extra Gems */
  'live-mid': {
    goldenQuest: {
      ...BASE,
      status: 'LIVE',
      user: { preRegistered: true, preRegGems: 5, reminderSet: true, storySeen: true, liveIntroSeen: true, referralCode: 'GSFEST-7K2' },
      wallet: {
        gems: [
          ...PRE_REG_GEMS_OPENED,
          { id: 'g6', source: 'CARD', earnedAt: '2026-10-19T19:14:00+05:30', openedAt: '2026-10-19T19:15:00+05:30', reward: { name: '250 Sats', detail: 'Added to your Bitcoin balance', kind: 'SATS' } },
          { id: 'g7', source: 'CARD', earnedAt: '2026-10-21T13:02:00+05:30', openedAt: '2026-10-21T13:03:00+05:30', reward: { name: '₹200 Flipkart voucher', detail: 'Saved under Vouchers', kind: 'VOUCHER' } },
          { id: 'g8', source: 'CARD', earnedAt: '2026-10-23T20:41:00+05:30', openedAt: '2026-10-23T20:42:00+05:30', reward: { name: '2× SuperCoins for a day', detail: 'Active on your next card spend', kind: 'BOOST' } },
          { id: 'g9', source: 'CARD', earnedAt: '2026-10-25T11:20:00+05:30', openedAt: null, reward: null },
          { id: 'g10', source: 'VOUCHER', earnedAt: '2026-10-20T10:05:00+05:30', openedAt: null, reward: null },
          { id: 'g11', source: 'VOUCHER', earnedAt: '2026-10-24T18:30:00+05:30', openedAt: null, reward: null },
          { id: 'g12', source: 'REFERRAL', earnedAt: '2026-10-19T09:00:00+05:30', openedAt: '2026-10-19T09:01:00+05:30', reward: { name: '₹25 in Gold', detail: 'Added to your gold vault', kind: 'GOLD' } },
          { id: 'g13', source: 'REFERRAL', earnedAt: '2026-10-20T09:00:00+05:30', openedAt: '2026-10-20T09:01:00+05:30', reward: { name: '100 Sats', detail: 'Added to your Bitcoin balance', kind: 'SATS' } },
          { id: 'g14', source: 'REFERRAL', earnedAt: '2026-10-22T09:00:00+05:30', openedAt: null, reward: null },
          { id: 'g15', source: 'REFERRAL', earnedAt: '2026-10-23T09:00:00+05:30', openedAt: null, reward: null },
          { id: 'g16', source: 'REFERRAL', earnedAt: '2026-10-25T16:40:00+05:30', openedAt: null, reward: null },
          { id: 'g17', source: 'REFERRAL', earnedAt: '2026-10-26T12:10:00+05:30', openedAt: null, reward: null },
        ],
        chests: [
          { id: 'c1', streak: 'card', earnedAt: '2026-10-25T11:20:00+05:30', celebrated: true, result: null },
          { id: 'c2', streak: 'ref', earnedAt: '2026-10-23T09:00:00+05:30', celebrated: true, result: null },
        ],
      },
      streaks: [
        { ...CARD, progress: 4, completed: true },
        { ...VOUCHERS, progress: 2 },
        GOLD,
        { ...REFERRALS, progress: 4, completed: true, extraGems: 2 },
      ],
    },
  },

  /* LIVE · the Card streak just completed: "Gold Chest earned" on open */
  'live-chest': {
    goldenQuest: {
      ...BASE,
      status: 'LIVE',
      user: { preRegistered: true, preRegGems: 5, reminderSet: true, storySeen: true, liveIntroSeen: true, referralCode: 'GSFEST-7K2' },
      wallet: {
        gems: [
          ...PRE_REG_GEMS_OPENED,
          { id: 'g6', source: 'CARD', earnedAt: '2026-10-19T19:14:00+05:30', openedAt: '2026-10-19T19:15:00+05:30', reward: { name: '250 Sats', detail: 'Added to your Bitcoin balance', kind: 'SATS' } },
          { id: 'g7', source: 'CARD', earnedAt: '2026-10-21T13:02:00+05:30', openedAt: '2026-10-21T13:03:00+05:30', reward: { name: '₹200 Flipkart voucher', detail: 'Saved under Vouchers', kind: 'VOUCHER' } },
          { id: 'g8', source: 'CARD', earnedAt: '2026-10-23T20:41:00+05:30', openedAt: null, reward: null },
          { id: 'g9', source: 'CARD', earnedAt: '2026-10-25T11:20:00+05:30', openedAt: null, reward: null },
        ],
        chests: [
          /* the user hasn't seen this one yet → celebration on open */
          { id: 'c1', streak: 'card', earnedAt: '2026-10-25T11:20:00+05:30', celebrated: false, result: null },
        ],
      },
      streaks: [{ ...CARD, progress: 4, completed: true }, VOUCHERS, GOLD, REFERRALS],
    },
  },

  /* LIVE · one step from a chest: Card 3/4, Gold 1/4 */
  'live-edge': {
    goldenQuest: {
      ...BASE,
      status: 'LIVE',
      user: { preRegistered: true, preRegGems: 5, reminderSet: true, storySeen: true, liveIntroSeen: true, referralCode: 'GSFEST-7K2' },
      wallet: {
        gems: [
          ...PRE_REG_GEMS_OPENED,
          { id: 'g6', source: 'CARD', earnedAt: '2026-10-19T19:14:00+05:30', openedAt: '2026-10-19T19:15:00+05:30', reward: { name: '250 Sats', detail: 'Added to your Bitcoin balance', kind: 'SATS' } },
          { id: 'g7', source: 'CARD', earnedAt: '2026-10-21T13:02:00+05:30', openedAt: '2026-10-21T13:03:00+05:30', reward: { name: '₹200 Flipkart voucher', detail: 'Saved under Vouchers', kind: 'VOUCHER' } },
          { id: 'g8', source: 'CARD', earnedAt: '2026-10-23T20:41:00+05:30', openedAt: null, reward: null },
          { id: 'g9', source: 'GOLD', earnedAt: '2026-10-20T10:05:00+05:30', openedAt: null, reward: null },
        ],
        chests: [],
      },
      streaks: [{ ...CARD, progress: 3 }, VOUCHERS, { ...GOLD, progress: 1 }, REFERRALS],
    },
  },

  /* ENDED · Card and Vouchers done; the Card chest won Grand 3, the Vouchers chest won Sats */
  ended: {
    goldenQuest: {
      ...BASE,
      status: 'ENDED',
      user: { preRegistered: true, preRegGems: 5, reminderSet: false, storySeen: true, liveIntroSeen: true, referralCode: 'GSFEST-7K2' },
      wallet: {
        gems: [
          ...PRE_REG_GEMS_OPENED,
          { id: 'g6', source: 'CARD', earnedAt: '2026-10-19T19:14:00+05:30', openedAt: '2026-10-19T19:15:00+05:30', reward: { name: '250 Sats', detail: 'Added to your Bitcoin balance', kind: 'SATS' } },
          { id: 'g7', source: 'CARD', earnedAt: '2026-10-21T13:02:00+05:30', openedAt: '2026-10-21T13:03:00+05:30', reward: { name: '₹200 Flipkart voucher', detail: 'Saved under Vouchers', kind: 'VOUCHER' } },
          { id: 'g8', source: 'CARD', earnedAt: '2026-10-23T20:41:00+05:30', openedAt: '2026-10-23T20:42:00+05:30', reward: { name: '2× SuperCoins for a day', detail: 'Active on your next card spend', kind: 'BOOST' } },
          { id: 'g9', source: 'CARD', earnedAt: '2026-10-25T11:20:00+05:30', openedAt: '2026-10-25T11:21:00+05:30', reward: { name: '₹25 in Gold', detail: 'Added to your gold vault', kind: 'GOLD' } },
          { id: 'g10', source: 'VOUCHER', earnedAt: '2026-10-20T10:05:00+05:30', openedAt: '2026-10-20T10:06:00+05:30', reward: { name: '100 Sats', detail: 'Added to your Bitcoin balance', kind: 'SATS' } },
          { id: 'g11', source: 'VOUCHER', earnedAt: '2026-10-24T18:30:00+05:30', openedAt: '2026-10-24T18:31:00+05:30', reward: { name: '₹100 Amazon voucher', detail: 'Saved under Vouchers', kind: 'VOUCHER' } },
          { id: 'g12', source: 'VOUCHER', earnedAt: '2026-11-02T18:30:00+05:30', openedAt: '2026-11-02T18:31:00+05:30', reward: { name: '₹50 in Gold', detail: 'Added to your gold vault', kind: 'GOLD' } },
          { id: 'g13', source: 'VOUCHER', earnedAt: '2026-11-08T18:30:00+05:30', openedAt: null, reward: null },
          { id: 'g14', source: 'REFERRAL', earnedAt: '2026-10-19T09:00:00+05:30', openedAt: '2026-10-19T09:01:00+05:30', reward: { name: '₹10 cashback', detail: 'Credited to your card', kind: 'CASHBACK' } },
          { id: 'g15', source: 'REFERRAL', earnedAt: '2026-10-20T09:00:00+05:30', openedAt: '2026-10-20T09:01:00+05:30', reward: { name: '250 Sats', detail: 'Added to your Bitcoin balance', kind: 'SATS' } },
        ],
        chests: [
          { id: 'c1', streak: 'card', earnedAt: '2026-10-25T11:20:00+05:30', celebrated: true, result: { kind: 'GRAND', prize: 'Apple Watch SE', rank: 3 } },
          { id: 'c2', streak: 'voucher', earnedAt: '2026-11-08T18:30:00+05:30', celebrated: true, result: { kind: 'SATS', prize: '500 Sats', rank: null } },
        ],
      },
      streaks: [
        { ...CARD, progress: 4, completed: true },
        { ...VOUCHERS, progress: 4, completed: true },
        GOLD,
        { ...REFERRALS, progress: 2 },
      ],
      draw: {
        drawnAt: '2026-11-13T20:00:00+05:30',
        /* how this user did; `rank` points at their row in `winners`, shown as "You" */
        you: {
          won: true,
          rank: 3,
          prize: 'Apple Watch SE',
          title: 'You won an Apple Watch SE',
          text: 'Your Card streak chest was drawn Grand 3. Shipping to your registered address.',
          kicker: 'Delivery details on their way by SMS',
        },
        winners: WINNERS,
      },
    },
  },

  /* ENDED · played, but no chest in the draw */
  'ended-none': {
    goldenQuest: {
      ...BASE,
      status: 'ENDED',
      user: { preRegistered: true, preRegGems: 5, reminderSet: false, storySeen: true, liveIntroSeen: true, referralCode: 'GSFEST-7K2' },
      wallet: {
        gems: [
          ...PRE_REG_GEMS_OPENED,
          { id: 'g6', source: 'CARD', earnedAt: '2026-10-19T19:14:00+05:30', openedAt: '2026-10-19T19:15:00+05:30', reward: { name: '250 Sats', detail: 'Added to your Bitcoin balance', kind: 'SATS' } },
        ],
        chests: [],
      },
      streaks: [{ ...CARD, progress: 1 }, VOUCHERS, GOLD, REFERRALS],
      draw: {
        drawnAt: '2026-11-13T20:00:00+05:30',
        you: {
          won: false,
          rank: null,
          prize: null,
          title: 'No grand prize this time',
          text: 'You had no chests in the draw. Your Gems and their rewards are still yours.',
          kicker: 'See you at the next festival',
        },
        winners: WINNERS,
      },
    },
  },
};
